var _excluded = ["children"],
    _excluded2 = ["error", "labelProps", "aria-label", "helperTextProps", "maxChars", "isDisabled", "isReadOnly", "isViewMode", "size", "name", "colSpan", "columns", "isRequired", "showOptionalIndicator", "children", "id", "value"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) { symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); } keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }

function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }

import { useFormControlContext } from '@chakra-ui/react';
import React, { useMemo } from 'react';
import { Group } from '../../../containers';
import { forwardRef } from '../../../utils';
import { getAutoCompleteProps } from '../../../utils/form-field-utils';
import { drillProps } from '../../../utils/render-utils';
import { Text, Typography } from '../../dataDisplay';
import { ConditionalWrapper } from '../ConditionalWrapper';
import { FormControl } from '../FormControl';
import { FormControlContext } from '../FormControl/FormControlContext';
import { FormErrorMessage } from '../FormError';
/**
 * @private For internal use only.
 */
// eslint-disable-next-line @typescript-eslint/naming-convention

import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";

var InputComp = _ref => {
  var {
    children
  } = _ref,
      props = _objectWithoutProperties(_ref, _excluded);

  var {
    isInvalid
  } = useFormControlContext();
  return /*#__PURE__*/_jsx(_Fragment, {
    children: drillProps(children, _objectSpread({
      isInvalid
    }, props))
  });
};
/**
 * @private For internal use only.
 */


export var _FormField = forwardRef((_ref2, ref) => {
  var _length;

  var {
    error,
    labelProps,
    'aria-label': ariaLabel,
    helperTextProps,
    maxChars,
    isDisabled,
    isReadOnly,
    isViewMode,
    size,
    name,
    colSpan = 1,
    columns = 1,
    isRequired,
    showOptionalIndicator,
    children,
    id,
    value
  } = _ref2,
      props = _objectWithoutProperties(_ref2, _excluded2);

  var isInvalid = !!error;

  var getTestId = prefix => name && "".concat(prefix, "-").concat(name);

  var charsOutOfMaxChars = maxChars && "".concat((_length = value === null || value === void 0 ? void 0 : value.length) !== null && _length !== void 0 ? _length : '0', "/").concat(maxChars); // eslint-disable-next-line react-hooks/exhaustive-deps

  var autoCompleteProps = useMemo(() => getAutoCompleteProps(name), []); // Create IDs for the helper text and max chars elements

  var helperTextId = helperTextProps !== null && helperTextProps !== void 0 && helperTextProps.label ? "".concat(id, "-helper-text") : undefined;
  var maxCharsId = charsOutOfMaxChars ? "".concat(id, "-max-chars") : undefined;
  /**
   * `aria-describedby` is necessary to associate the input with the helper text and max chars elements.
   * attribute expects an ([ID reference list](https://www.w3.org/TR/wai-aria/#typemapping)) as it's value. Multiple IDs can be provided, separated by spaces.
   */

  var ariaDescribedby = "".concat(helperTextId !== null && helperTextId !== void 0 ? helperTextId : '', " ").concat(maxCharsId !== null && maxCharsId !== void 0 ? maxCharsId : '').trim();
  return /*#__PURE__*/_jsx(FormControlContext.Provider, {
    value: {
      maxChars,
      isInvalid
    },
    children: /*#__PURE__*/_jsxs(FormControl, _objectSpread(_objectSpread({
      gridColumn: {
        xs: '1 / 1',
        s: "span ".concat(colSpan, " / span ").concat(columns)
      } // `minWidth` is necessary to support text-ellipsis in descendant elements
      ,
      minWidth: 0,
      size: size,
      isInvalid: isInvalid,
      isDisabled: isDisabled,
      isReadOnly: isReadOnly,
      isViewMode: isViewMode,
      "data-testid": getTestId('form-field'),
      "data-component": "_FormField",
      id: id
    }, props), {}, {
      ref: ref,
      children: [labelProps && /*#__PURE__*/_jsx(Typography.Label, _objectSpread({
        "data-testid": getTestId('form-label'),
        isInvalid: isInvalid,
        isReadOnly: isReadOnly,
        isViewMode: isViewMode,
        isDisabled: isDisabled,
        description: isViewMode ? undefined : isRequired ? '*' : showOptionalIndicator ? '(optional)' : undefined,
        id: id
      }, labelProps)), /*#__PURE__*/_jsxs(Group, {
        spacing: size === 'small' ? 'xxxs' : 'xxs',
        variant: "vertical",
        children: [/*#__PURE__*/_jsx(InputComp, _objectSpread(_objectSpread({}, autoCompleteProps), {}, {
          "aria-label": ariaLabel,
          "aria-describedby": ariaDescribedby,
          "aria-required": !isViewMode && isRequired,
          children: children
        })), /*#__PURE__*/_jsx(ConditionalWrapper, {
          condition: isInvalid || (helperTextProps === null || helperTextProps === void 0 ? void 0 : helperTextProps.label) && !isViewMode || !!maxChars,
          wrapper: children => /*#__PURE__*/_jsx(Group, {
            spacing: "m",
            justifyContent: isInvalid || helperTextProps !== null && helperTextProps !== void 0 && helperTextProps.label && !isViewMode ? 'space-between' : 'flex-end',
            children: children
          }),
          children: /*#__PURE__*/_jsxs(_Fragment, {
            children: [isInvalid ? /*#__PURE__*/_jsx(FormErrorMessage, {
              size: size,
              "data-testid": getTestId('form-error-message'),
              id: helperTextId,
              children: error === null || error === void 0 ? void 0 : error.message
            }) : helperTextProps !== null && helperTextProps !== void 0 && helperTextProps.label && !isViewMode ? /*#__PURE__*/_jsx(Typography.Description, _objectSpread(_objectSpread({
              "data-testid": getTestId('form-helper-text')
            }, helperTextProps), {}, {
              isInvalid: isInvalid,
              isReadOnly: isReadOnly,
              isDisabled: isDisabled,
              id: helperTextId,
              "aria-live": "assertive"
            })) : null, charsOutOfMaxChars && /*#__PURE__*/_jsx(Text, {
              textStyle: "body4",
              color: isInvalid ? 'critical.main' : isDisabled ? 'neutral.light' : 'neutral.darker',
              "data-testid": "max-chars",
              id: maxCharsId,
              children: charsOutOfMaxChars
            })]
          })
        })]
      })]
    }))
  });
});
_FormField.displayName = '_FormField';
//# sourceMappingURL=_FormField.js.map